/**
 * O fluxo da previsão: o que acontece em volta do interruptor "Previsão".
 *
 * A previsão é uma extrapolação a partir de UMA observação; com o loop tocando
 * ela não faz sentido. Então:
 * - ligar a previsão PARA o player principal e leva o mapa a um quadro que tem
 *   previsão (o exibido, ou o mais novo do loop que tem). A previsão liga
 *   PARADA na antecedência escolhida (a guardada, ou o padrão de 30 min): o
 *   que o usuário escolheu fica (29/09/2026; antes o fluxo começava sozinho, e
 *   a escolha sumia da tela);
 * - o fluxo só toca quando o usuário aperta tocar: a observação e cada
 *   antecedência que o quadro tem (+30 min → … → +3 h), uma pausa no fim, e de
 *   novo;
 * - pausar fica no passo que está na tela, que vira a escolha; pausar na
 *   observação não apaga a escolha;
 * - trocar de quadro à mão mantém a previsão, e o fluxo (se tocava) recomeça
 *   da observação do quadro novo;
 * - apertar play no player principal DESLIGA a previsão: o loop toca só com a
 *   observação (imagem e rastreio);
 * - abrir por um link (`k=rp60`) ou pelas preferências salvas mostra a
 *   antecedência do link, parada — o fluxo só começa por ação do usuário;
 * - a exportação e desligar a previsão tiram o fluxo de cena sem mexer na
 *   escolha: o vídeo sai com a antecedência escolhida.
 */
import { subscribeSelector } from '../lib/store';
import { goToFirst, goToFrame, goToLast, isExportLocked, pause } from '../loop/playback';
import { useLoopStore } from '../loop/store';
import { useGoesStore } from '../state/goes';
import { showToast } from '../state/toasts';
import { shownTimeOf, useTimeStore } from '../state/time';
import { marcarVisto } from '../ui/novidade';
import { BASE_RASTREIO } from './api';
import { antecedenciaEscolhida, antecedenciaNaTela, antecedenciasDisponiveis, useTrackingStore, type TrackingState } from './store';
import { antecedenciaValida, esperaDoPasso, proximoPasso, quadroDeOrigem, quadroMaisNovoComPrevisao } from './tempo';
import { ANTECEDENCIAS_PADRAO, estaNaZona, NOVIDADE_DA_ZONA } from './zona';

export type OrigemDaPrevisao = 'exibida' | 'mais-nova';

let relogio: ReturnType<typeof setTimeout> | null = null;
/**
 * A previsão foi pedida antes de o índice chegar: quando ele vier, vai ao
 * quadro de origem e escolhe a antecedência pedida (`h`; sem ela, a guardada).
 */
let pendente: { h?: number; origem: OrigemDaPrevisao } | null = null;
/** `verPrevisao` está ligando a previsão: o quadro já é o que ele escolheu, o assinante não pula. */
let ligandoPorVerPrevisao = false;

function pararRelogio(): void {
  if (relogio) clearTimeout(relogio);
  relogio = null;
}

function agendar(): void {
  pararRelogio();
  const s = useTrackingStore.getState();
  if (!s.previsao || !s.fluxo) return;
  relogio = setTimeout(() => {
    relogio = null;
    const agora = useTrackingStore.getState();
    if (!agora.previsao || !agora.fluxo) return;
    // Aba escondida ou previsão ainda baixando: o passo espera, sem andar no escuro.
    const pronto = agora.noQuadro.previsao === 'ok' && (typeof document === 'undefined' || document.visibilityState !== 'hidden');
    // Só pelas antecedências que o quadro tem: um passo sem polígono seria um mapa vazio.
    if (pronto) useTrackingStore.setState({ passo: proximoPasso(agora.passo, antecedenciasDisponiveis(agora)) });
    agendar();
  }, esperaDoPasso(s.passo, antecedenciasDisponiveis(s)));
}

/** O índice (em `frames`) do quadro de onde a previsão parte, ou −1 (sem índice, ou nenhum quadro com previsão). */
function origemDa(origem: OrigemDaPrevisao): number {
  const { indice } = useTrackingStore.getState();
  if (!indice) return -1;
  const { frames, index } = useTimeStore.getState();
  return origem === 'mais-nova' ? quadroMaisNovoComPrevisao(frames, indice.quadros) : quadroDeOrigem(frames, indice.quadros, index);
}

/** Leva o mapa ao quadro de onde a previsão parte. Falso se não há origem. */
function irAoQuadroDeOrigem(origem: OrigemDaPrevisao): boolean {
  const i = origemDa(origem);
  if (i < 0) return false;
  if (i !== useTimeStore.getState().index) goToFrame(i);
  return true;
}

/**
 * Põe na tela a antecedência disponível mais próxima de `h` — sem `h`, a
 * guardada. Para o fluxo e sai de "agora": é um pedido de ver a previsão.
 */
function escolher(h: number | undefined, oferecidas?: readonly number[]): void {
  const s = useTrackingStore.getState();
  s.setAntecedencia(h === undefined ? antecedenciaEscolhida(s) : antecedenciaValida(h, oferecidas ?? antecedenciasDisponiveis(s)));
}

/** Leva o mapa à origem mais nova, mantendo a antecedência (e o fluxo, se tocava). */
function seguirNaZona(): void {
  const { indice } = useTrackingStore.getState();
  if (!indice) return;
  const { frames, index } = useTimeStore.getState();
  const i = quadroMaisNovoComPrevisao(frames, indice.quadros);
  if (i >= 0 && i !== index) goToFrame(i);
}

/** O índice chegou com a previsão pedida antes dele. */
function resolverPendente(): void {
  const p = pendente;
  pendente = null;
  if (!p || !useTrackingStore.getState().previsao) return;
  irAoQuadroDeOrigem(p.origem);
  if (p.h !== undefined) escolher(p.h);
}

/** Atualiza `naOrigem`: a imagem na tela é a mais nova do loop com previsão? */
function marcarOrigem(): void {
  const { indice, naOrigem } = useTrackingStore.getState();
  const { frames, index } = useTimeStore.getState();
  const origem = indice ? quadroMaisNovoComPrevisao(frames, indice.quadros) : -1;
  const agora = origem >= 0 && index === origem;
  if (agora !== naOrigem) useTrackingStore.setState({ naOrigem: agora });
}

export function iniciarFluxoPrevisao(): () => void {
  marcarOrigem();
  const paradas = [
    subscribeSelector(useTimeStore, (s) => s.index, marcarOrigem),
    subscribeSelector(useTimeStore, (s) => s.frames, marcarOrigem),
    subscribeSelector(useTrackingStore, (s) => s.indice, marcarOrigem),
    useTrackingStore.subscribe((a, b) => {
      if (a.previsao && !b.previsao) {
        // Ligada pelo usuário (o estado do link é aplicado antes de este módulo começar).
        pause();
        // `verPrevisao` já levou o mapa à origem (e, sem índice, deixou o pedido dele pendente).
        // Ligar é ver a previsão MAIS NOVA: a de imagens anteriores não interessa (08/10/2026).
        if (a.indice) {
          if (!ligandoPorVerPrevisao) irAoQuadroDeOrigem('mais-nova');
        }
        else pendente ??= { origem: 'mais-nova' };
        // Parada na antecedência escolhida: o fluxo é do botão de tocar.
        if (a.fluxo || a.naObservacao) useTrackingStore.getState().pararFluxo();
      } else if (!a.previsao && b.previsao) {
        pendente = null;
        if (a.fluxo || a.naObservacao) useTrackingStore.getState().pararFluxo();
      }
      if (a.indice && a.indice !== b.indice && pendente) resolverPendente();
      if (a.fluxo !== b.fluxo || a.previsao !== b.previsao) agendar();
    }),
    // Imagem nova (a janela do loop andou) ou índice novo com previsão num quadro mais novo: quem
    // estava na previsão da linha do tempo continua nela, na nova origem — senão o índice do loop
    // salta para a imagem nova (sem rastreio ainda), a previsão some e o fluxo gira sem andar.
    useTimeStore.subscribe((a, b) => {
      if (a.frames === b.frames) return;
      const s = useTrackingStore.getState();
      if (naPrevisaoDaBarra(s, b.frames, b.index)) seguirNaZona();
    }),
    useTrackingStore.subscribe((a, b) => {
      if (a.indice === b.indice || !b.indice || !a.indice) return;
      const { frames, index } = useTimeStore.getState();
      if (naPrevisaoDaBarra(b, frames, index)) seguirNaZona();
    }),
    // Navegar à mão desfaz um pedido que esperava o índice: ele não pode, minutos depois, puxar o mapa de volta.
    subscribeSelector(useTimeStore, (s) => s.index, () => {
      pendente = null;
    }),
    // Outro quadro na tela: a extrapolação recomeça da observação dele.
    subscribeSelector(useTimeStore, (s) => shownTimeOf(s), () => {
      const s = useTrackingStore.getState();
      if (!s.previsao || !s.fluxo) return;
      if (s.passo !== 0) useTrackingStore.setState({ passo: 0 });
      agendar();
    }),
    // O player principal e a extrapolação não tocam juntos.
    subscribeSelector(useLoopStore, (s) => s.playing, (tocando) => {
      if (!tocando || !useTrackingStore.getState().previsao) return;
      useTrackingStore.getState().setPrevisao(false);
      showToast({ id: 'previsao-desligada', kind: 'info', message: 'Previsão desligada: o loop mostra só a observação.' });
    }),
    // Exportação: quadros determinísticos, com a antecedência escolhida.
    subscribeSelector(useLoopStore, (s) => s.exportLocked, (travado) => {
      // Não é pausa do usuário: a escolha dele não muda.
      if (travado && useTrackingStore.getState().fluxo) useTrackingStore.getState().pararFluxo();
    }),
  ];
  agendar();
  return () => {
    paradas.forEach((p) => p());
    pararRelogio();
    pendente = null;
  };
}

// ---------------------------------------------------------------------------
// A previsão na linha do tempo (a zona à direita do eixo das imagens) e a ficha do sistema
// ---------------------------------------------------------------------------

/**
 * A zona está na barra: a Previsão está LIGADA (só então — pedido do usuário, 08/10/2026), há
 * máquina de rastreio, o satélite está ligado e o período não é personalizado.
 */
export function zonaNaBarra(): boolean {
  return (
    useTrackingStore.getState().previsao &&
    !!BASE_RASTREIO &&
    useGoesStore.getState().layers.some((l) => l.visible) &&
    useLoopStore.getState().period !== 'custom'
  );
}

/**
 * Pausa, vai ao quadro de origem, liga a previsão e escolhe a antecedência
 * disponível mais próxima de `h` (padrão: a guardada).
 * - `'exibida'`: o quadro na tela, se ele tem previsão; senão o mais novo que
 *   tem (o interruptor "Previsão" e a ficha);
 * - `'mais-nova'`: sempre o mais novo que tem (a zona da linha do tempo).
 *
 * A ORDEM importa: primeiro o quadro, depois `previsao` — o assinante acha a
 * previsão no quadro exibido e não pula —, por fim a antecedência. Sem índice
 * o pedido fica pendente e se resolve quando ele chega (ligar a previsão
 * dispara a carga). Falso se o índice chegou e nenhum quadro tem previsão.
 */
export function verPrevisao(h?: number, origem: OrigemDaPrevisao = 'exibida'): boolean {
  pause();
  const s = useTrackingStore.getState();
  if (!s.indice) {
    pendente = { h, origem };
    if (!s.previsao) s.setPrevisao(true);
    marcarVisto(NOVIDADE_DA_ZONA.id);
    // Já vai para o link; quando o índice chegar, vira a disponível mais próxima.
    escolher(h);
    return true;
  }
  const i = origemDa(origem);
  if (i < 0) return false;
  pendente = null;
  marcarVisto(NOVIDADE_DA_ZONA.id);
  const outroQuadro = i !== useTimeStore.getState().index;
  if (outroQuadro) goToFrame(i);
  if (!useTrackingStore.getState().previsao) {
    ligandoPorVerPrevisao = true;
    try {
      useTrackingStore.getState().setPrevisao(true);
    } finally {
      ligandoPorVerPrevisao = false;
    }
  }
  // Trocando de quadro, `hNoQuadro` ainda é o do arquivo que sai da tela (talvez um antigo, de
  // passo 10 min): arredonda pelo índice; o arquivo da origem acerta a tela quando chegar.
  escolher(h, outroQuadro ? s.indice.antecedencias : undefined);
  return true;
}

/**
 * Sai da previsão: desliga-a (a antecedência continua guardada) e fica no
 * quadro de origem, ou vai à imagem mais recente ("Agora").
 */
export function sairDaPrevisao(para: 'origem' | 'ultimo'): void {
  pendente = null;
  const s = useTrackingStore.getState();
  if (s.previsao) s.setPrevisao(false);
  if (para === 'ultimo') goToLast();
}

/**
 * O ▶ da zona: entra na previsão da imagem mais nova e toca o fluxo, que
 * começa pela observação. Não troca a antecedência guardada: pausar na
 * observação volta a ela, como no painel.
 */
export function tocarPrevisao(): void {
  if (verPrevisao(undefined, 'mais-nova')) useTrackingStore.getState().setFluxo(true);
}

/** O cursor do tempo está na zona da previsão (e não no eixo das imagens). */
export function cursorNaZona(): boolean {
  const s = useTrackingStore.getState();
  if (!s.previsao || !s.indice || !zonaNaBarra()) return false;
  const { frames, index } = useTimeStore.getState();
  return estaNaZona(s.previsao, antecedenciaNaTela(s), index, quadroMaisNovoComPrevisao(frames, s.indice.quadros));
}

/**
 * O usuário está "na previsão da linha do tempo": o cursor na zona, ou o fluxo
 * da zona tocando — no passo 0 (a observação) o cursor volta à imagem de
 * origem, mas o fluxo ainda é da zona. É o predicado do teclado, do toque no
 * eixo e de quem segura a previsão quando chega imagem nova.
 */
function naPrevisaoDaBarra(
  s: Pick<TrackingState, 'previsao' | 'fluxo' | 'passo' | 'antecedencia' | 'naObservacao' | 'indice' | 'hNoQuadro'>,
  frames: readonly number[],
  index: number,
): boolean {
  if (!s.previsao || !s.indice || !zonaNaBarra()) return false;
  const origem = quadroMaisNovoComPrevisao(frames, s.indice.quadros);
  return origem >= 0 && index === origem && (s.fluxo || estaNaZona(s.previsao, antecedenciaNaTela(s), index, origem));
}

/** `naPrevisaoDaBarra` agora. */
export function naZonaOuNoFluxo(): boolean {
  const { frames, index } = useTimeStore.getState();
  return naPrevisaoDaBarra(useTrackingStore.getState(), frames, index);
}

function focar(seletor: string): void {
  if (typeof document === 'undefined') return;
  document.querySelector<HTMLElement>(seletor)?.focus({ preventScroll: true });
}

/** Seletor do slider da zona (`ZonaDaPrevisao.tsx`). */
export const SELETOR_DA_ZONA = '[data-zona-previsao]';
const SELETOR_DO_EIXO = '[role="slider"][aria-label="Quadro da animação"]';

/**
 * Teclado da zona, consultado antes dos atalhos do loop e do eixo das
 * imagens. Verdadeiro = a tecla foi tratada.
 * - Na imagem mais recente, → entra na primeira antecedência (o foco vai à zona).
 * - Na zona: ←/→ andam pelas antecedências que o quadro tem; ← na primeira
 *   volta à imagem de origem, sem previsão; Home/End desligam a previsão e vão
 *   à ponta do loop; espaço toca ou pausa o fluxo.
 * `focar: false` (botões): a tecla não leva o foco junto.
 */
export function teclaDaPrevisao(tecla: string, opcoes: { focar?: boolean } = {}): boolean {
  if (isExportLocked() || !zonaNaBarra()) return false;
  const comFoco = opcoes.focar ?? true;
  const s = useTrackingStore.getState();
  const { frames, index } = useTimeStore.getState();
  const origem = s.indice ? quadroMaisNovoComPrevisao(frames, s.indice.quadros) : -1;
  const espaco = tecla === ' ' || tecla === 'Spacebar';
  // Tocando, o passo 0 é a observação, mas o fluxo ainda é da zona.
  const naZona = naPrevisaoDaBarra(s, frames, index);
  if (naZona) {
    const disponiveis = antecedenciasDisponiveis(s);
    const atual = antecedenciaNaTela(s);
    const i = disponiveis.indexOf(atual);
    // Sair da zona pelo teclado não desliga a previsão: no eixo das imagens ela continua, a
    // partir de cada imagem anterior (a zona só existe com ela ligada).
    const sair = (para: () => void) => {
      if (s.fluxo) s.setFluxo(false);
      para();
      if (comFoco) focar(SELETOR_DO_EIXO);
    };
    if (tecla === 'ArrowRight' || tecla === 'ArrowUp') s.setAntecedencia(disponiveis[Math.min(i + 1, disponiveis.length - 1)] ?? atual);
    else if (tecla === 'PageUp') s.setAntecedencia(disponiveis[disponiveis.length - 1] ?? atual);
    else if (tecla === 'ArrowLeft' || tecla === 'ArrowDown' || tecla === 'PageDown') {
      if (i > 0 && tecla !== 'PageDown') s.setAntecedencia(disponiveis[i - 1]);
      // Antes da primeira: a imagem anterior à origem, com a previsão feita a partir dela.
      else if (index > 0) sair(() => goToFrame(index - 1));
      else return true;
    } else if (tecla === 'Home') sair(goToFirst);
    else if (tecla === 'End') sair(goToLast);
    else if (espaco) {
      if (s.fluxo) s.setFluxo(false);
      else tocarPrevisao();
    } else return false;
    return true;
  }
  // Na ponta do eixo das imagens, → continua para o futuro.
  if ((tecla === 'ArrowRight' || tecla === 'ArrowUp') && frames.length > 0 && index === frames.length - 1 && (!s.indice || origem >= 0)) {
    const primeira = (s.indice?.antecedencias.length ? s.indice.antecedencias : ANTECEDENCIAS_PADRAO).find((a) => a > 0);
    if (!verPrevisao(primeira, 'mais-nova')) return false;
    if (comFoco) focar(SELETOR_DA_ZONA);
    return true;
  }
  return false;
}
